import { Copy, LifeBuoy, Printer } from "lucide-react";
import { useEffect, useState } from "react";

import { attentionSelector, designate } from "../components/attention";
import { Callout } from "../components/Callout";
import { Checkbox } from "../components/Checkbox";
import { Presence } from "../components/presence";
import { Button } from "../components/ui";
import { t } from "../i18n/i18n";
import { LicenceFrame } from "../licence/ActivationScreen";
import { AuthTitle } from "./AuthForm";

/** Code de secours de l'unité (docs/17 §5.5, §8.3), affiché après l'amorçage et après chaque usage du code : une
 *  seule fois, jamais rangé dans le navigateur (il ne vit que dans l'état de cet écran). « Accéder à Generex » n'est
 *  actif qu'une fois la case cochée ; avant, un appui désigne la case. Quitter la page avant demande confirmation. */
export function RecoveryCodeStep({ code, onDone }: { code: string; onDone: () => void }) {
  const [noted, setNoted] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, []);
  const copy = () => {
    void navigator.clipboard?.writeText(code).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };
  return (
    <LicenceFrame>
      <AuthTitle icon={<LifeBuoy size={16} />}>{t("Code de secours de l'unité")}</AuthTitle>
      <p className="mt-1.5 text-ui leading-relaxed text-ink-soft">
        {t("Notez ce code et confiez-le au commandement de l'unité. Avec lui, un administrateur qui a perdu son mot de passe reprend la main sans aide extérieure, quand aucun autre administrateur n'est joignable. Il ne sera plus affiché.")}
      </p>
      <div className="mt-4 rounded-lg border border-line bg-canvas px-3 py-3 text-center font-mono text-heading tracking-wider text-ink tabular-nums select-all">
        {code}
      </div>
      <div className="mt-2 flex flex-wrap justify-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => window.print()}>
          <Printer size={14} /> {t("Imprimer")}
        </Button>
        <Button variant="secondary" size="sm" onClick={copy}>
          <Copy size={14} /> {t("Copier")}
        </Button>
      </div>
      <Presence show={copied}>
        {copied && (
          <Callout tone="ok" className="mt-2">
            {t("Code copié : collez-le là où il sera gardé sous pli, puis effacez-le du presse-papiers.")}
          </Callout>
        )}
      </Presence>
      <div className="mt-4 space-y-3 border-t border-line pt-4">
        <Checkbox data-attention="auth-recovery-noted" checked={noted} onChange={setNoted} label={t("J'ai imprimé ou recopié ce code")} />
        <Button className="w-full" blocked={!noted} onBlocked={() => designate(attentionSelector("auth-recovery-noted"))} onClick={onDone}>
          {t("Accéder à Generex")}
        </Button>
      </div>
    </LicenceFrame>
  );
}
